import React, { useEffect, useRef, useState } from 'react';
import { authAPI } from './api';
import './AuthScreen.css';

/* ── SEO constants ─────────────────────────────── */
const SITE_URL  = typeof window !== 'undefined' ? window.location.origin : 'https://efinpro.triyoginnovation.com';
const SEO_TITLE = 'e-FinPro | Contract-to-Invoice Finance Platform for NGOs, PMUs & Consulting Firms';
const SEO_DESC  = 'e-FinPro is India\'s purpose-built contract-to-cash platform — manage agreements, raise GST invoices, track TDS receipts, run a bank ledger, and export Tally-ready reports from one workspace.';
const SEO_KEYS  = 'contract management software india, GST invoice software, TDS tracking, NGO finance software, PMU billing, donor-funded contracts, consulting invoice software, Tally export';

const injectMeta = (attr, key, val) => {
  let t = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!t) { t = document.createElement('meta'); t.setAttribute(attr, key); document.head.appendChild(t); }
  t.setAttribute('content', val);
};
const injectCanonical = (href) => {
  let l = document.head.querySelector('link[rel="canonical"]');
  if (!l) { l = document.createElement('link'); l.setAttribute('rel','canonical'); document.head.appendChild(l); }
  l.setAttribute('href', href);
};
const injectLD = (data) => {
  let s = document.getElementById('efinpro-ld');
  if (!s) { s = document.createElement('script'); s.type='application/ld+json'; s.id='efinpro-ld'; document.head.appendChild(s); }
  s.textContent = JSON.stringify(data);
};

/* ── Static content ─────────────────────────────── */
const FEATURES = [
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 17H7A5 5 0 0 1 7 7h2"/><path d="M15 7h2a5 5 0 0 1 0 10h-2"/><line x1="8" y1="12" x2="16" y2="12"/></svg>, cls:'fi-navy',   title:'Contract Workspace', body:'Maintain agreements with milestones, deliverables, amendments, and document registers — all linked to billing.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>, cls:'fi-green',  title:'GST Invoice Engine', body:'Raise client-ready invoices with IGST/CGST logic, auto-sequential numbering, multi-currency, and authorised signatory.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>, cls:'fi-amber',  title:'TDS-Aware Receipts', body:'Record payment receipts with client-deducted TDS, net settlement amounts, and reconciliation status.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>, cls:'fi-purple', title:'Bank Ledger', body:'Opening balance + invoice credits − expense debits = live running bank balance. Every entry is auditable.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>, cls:'fi-teal',   title:'Export-Ready Reports', body:'Download invoice, GST, TDS, bank ledger, and Tally XML exports by date, range, or financial year.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, cls:'fi-red',    title:'Multi-Firm Access', body:'Each firm has isolated data. Add team members, set admin roles, and manage authorised signatories with full audit control.' },
];

const WORKFLOW = [
  { n:'01', title:'Create Contract', body:'Capture client, billing, and agreement metadata with country, currency, and GST settings.' },
  { n:'02', title:'Build Invoices', body:'Generate GST invoices from contract — client details, signatory, and prefix auto-fill.' },
  { n:'03', title:'Record Receipts', body:'Log payments with TDS deductions and settlement status against each invoice.' },
  { n:'04', title:'Export & Report', body:'Download invoice, GST, TDS, ledger, and Tally-ready files for finance and audit.' },
];

const INDUSTRIES = [
  { icon:'🏛️', title:'NGOs & Donor-Funded Programmes', body:'Manage grant-linked contracts, donor billing references, milestone tracking, and receivable reconciliation from one workspace.' },
  { icon:'🏗️', title:'Government PMUs & Public Sector', body:'Handle multi-vendor agreements, compliance documentation, amendment trails, and structured billing against government work orders.' },
  { icon:'📐', title:'Consulting Firms & Consortiums', body:'Track project-based billing, retainer agreements, milestone invoicing, and TDS-adjusted receipts across client engagements.' },
];

const FAQS = [
  { q:'Who is e-FinPro built for?', a:'Teams managing contract-led finance operations — NGOs, PMUs, public sector missions, and consulting firms where each invoice must trace back to a contract and every receipt must record TDS.' },
  { q:'Does it handle Indian tax requirements?', a:'Yes. GST invoicing (IGST/CGST), TDS deduction tracking, settlement recording, and Tally XML export are core to the platform. GST can be toggled off for international billing.' },
  { q:'Can multiple firms use the same platform?', a:'Yes. Each firm has a fully isolated workspace. Admins add team members and signatories. No firm can access another\'s data.' },
  { q:'How do we export to Tally?', a:'From the Reports menu, download a Sales Voucher XML file formatted for Tally ERP import — one voucher per invoice with debtor, sales ledger, and IGST entries.' },
  { q:'Is there a free trial?', a:'Register your firm directly — no credit card required. Start with contracts and invoices, then expand into receipts, ledger, and reporting.' },
];

/* ── Auth form sub-component ─────────────────────── */
function AuthForm({ onAuth, scrollToTop }) {
  const [tab,     setTab]     = useState('login');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [step,    setStep]    = useState(1);
  const [loginData, setLoginData] = useState({ email:'', password:'' });
  const [regData,   setRegData]   = useState({ firmName:'', firmEmail:'', adminName:'', adminEmail:'', password:'', confirmPassword:'' });

  const setL = (k,v) => setLoginData(p => ({...p,[k]:v}));
  const setR = (k,v) => setRegData(p => ({...p,[k]:v}));

  const switchTab = (t) => { setTab(t); setError(''); setStep(1); };

  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await authAPI.login({ email: loginData.email, password: loginData.password });
      localStorage.setItem('ti_token', data.token);
      onAuth(data);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault(); setError('');
    if (step === 1) {
      if (regData.password !== regData.confirmPassword) { setError('Passwords do not match'); return; }
      if (regData.password.length < 8) { setError('Password must be at least 8 characters'); return; }
      setStep(2); return;
    }
    setLoading(true);
    try {
      const data = await authAPI.register({ firmName: regData.firmName, firmEmail: regData.firmEmail || regData.adminEmail, adminName: regData.adminName, adminEmail: regData.adminEmail, password: regData.password });
      localStorage.setItem('ti_token', data.token);
      onAuth(data);
    } catch (err) { setError(err.message); setStep(1); }
    finally { setLoading(false); }
  };

  return (
    <div className="lp-auth-card">
      {/* Tabs */}
      <div className="lp-auth-tabs">
        <button className={`lp-auth-tab${tab==='login'?' active':''}`} onClick={() => switchTab('login')}>Sign In</button>
        <button className={`lp-auth-tab${tab==='register'?' active':''}`} onClick={() => switchTab('register')}>Register Free</button>
      </div>

      <div className="lp-auth-body">
        {tab === 'login' && <><div className="lp-auth-title">Welcome back</div><div className="lp-auth-subtitle">Sign in to your firm workspace</div></>}
        {tab === 'register' && step === 1 && <><div className="lp-auth-title">Create your account</div><div className="lp-auth-subtitle">Step 1 of 2 — your admin credentials</div></>}
        {tab === 'register' && step === 2 && <><div className="lp-auth-title">Your firm details</div><div className="lp-auth-subtitle">Step 2 of 2 — we'll set up your workspace</div></>}

        {tab === 'register' && (
          <div className="lp-steps">
            <div className={`lp-step ${step >= 1 ? 'done':''}`}><span className="lp-step-num">1</span><span>Account</span></div>
            <div className="lp-step-line" />
            <div className={`lp-step ${step >= 2 ? 'done':''}`}><span className="lp-step-num">2</span><span>Firm</span></div>
          </div>
        )}

        {error && <div className="lp-auth-error">{error}</div>}

        {/* Login */}
        {tab === 'login' && (
          <form onSubmit={handleLogin} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Email</label><input className="lp-inp" type="email" required autoFocus value={loginData.email} onChange={e=>setL('email',e.target.value)} placeholder="you@firm.com" /></div>
            <div className="lp-fg"><label className="lp-lbl">Password</label><input className="lp-inp" type="password" required value={loginData.password} onChange={e=>setL('password',e.target.value)} placeholder="••••••••" /></div>
            <button className="lp-submit" type="submit" disabled={loading}>{loading ? 'Signing in…':'Sign In →'}</button>
            <p className="lp-auth-switch">No account? <button type="button" onClick={()=>switchTab('register')}>Register your firm free</button></p>
          </form>
        )}

        {/* Register step 1 */}
        {tab === 'register' && step === 1 && (
          <form onSubmit={handleRegister} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Your Full Name</label><input className="lp-inp" required autoFocus value={regData.adminName} onChange={e=>setR('adminName',e.target.value)} placeholder="e.g. N. S. Mehta" /></div>
            <div className="lp-fg"><label className="lp-lbl">Your Email</label><input className="lp-inp" type="email" required value={regData.adminEmail} onChange={e=>setR('adminEmail',e.target.value)} placeholder="you@firm.com" /></div>
            <div className="lp-grid2">
              <div className="lp-fg"><label className="lp-lbl">Password</label><input className="lp-inp" type="password" required value={regData.password} onChange={e=>setR('password',e.target.value)} placeholder="Min 8 chars" /></div>
              <div className="lp-fg"><label className="lp-lbl">Confirm</label><input className="lp-inp" type="password" required value={regData.confirmPassword} onChange={e=>setR('confirmPassword',e.target.value)} placeholder="Repeat" /></div>
            </div>
            <button className="lp-submit" type="submit">Continue →</button>
            <p className="lp-auth-switch">Already registered? <button type="button" onClick={()=>switchTab('login')}>Sign in</button></p>
          </form>
        )}

        {/* Register step 2 */}
        {tab === 'register' && step === 2 && (
          <form onSubmit={handleRegister} className="lp-auth-form">
            <div className="lp-fg"><label className="lp-lbl">Firm / Company Name *</label><input className="lp-inp" required autoFocus value={regData.firmName} onChange={e=>setR('firmName',e.target.value)} placeholder="e.g. Triyog Innovations Pvt Ltd" /></div>
            <div className="lp-fg"><label className="lp-lbl">Official Firm Email (optional)</label><input className="lp-inp" type="email" value={regData.firmEmail} onChange={e=>setR('firmEmail',e.target.value)} placeholder="info@firm.com" /></div>
            <p className="lp-auth-note">You can add logo, GST/PAN, bank details, and signatories after registration from Settings.</p>
            <div className="lp-grid2">
              <button type="button" className="lp-back-btn" onClick={()=>{setStep(1);setError('');}}>← Back</button>
              <button className="lp-submit" type="submit" disabled={loading}>{loading ? 'Creating…':'Create Firm →'}</button>
            </div>
          </form>
        )}
      </div>
      <div className="lp-auth-foot">🔒 SSL encrypted · No credit card required</div>
    </div>
  );
}

/* ── FAQ item ────────────────────────────────────── */
function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-faq-item">
      <button className="lp-faq-q" onClick={() => setOpen(v => !v)}>
        {q}<span className={`lp-faq-chevron${open?' open':''}`}>⌄</span>
      </button>
      {open && <div className="lp-faq-a">{a}</div>}
    </div>
  );
}

/* ── Main AuthScreen ─────────────────────────────── */
export default function AuthScreen({ onAuth }) {
  const authRef    = useRef(null);
  const featuresId = 'lp-features';
  const howId      = 'lp-how';
  const industriesId = 'lp-industries';

  useEffect(() => {
    document.title = SEO_TITLE;
    injectMeta('name','description', SEO_DESC);
    injectMeta('name','keywords', SEO_KEYS);
    injectMeta('name','robots','index, follow, max-image-preview:large');
    injectMeta('property','og:type','website');
    injectMeta('property','og:title', SEO_TITLE);
    injectMeta('property','og:description', SEO_DESC);
    injectMeta('property','og:url', `${SITE_URL}/`);
    injectMeta('property','og:site_name','e-FinPro');
    injectMeta('property','og:image', `${SITE_URL}/efinpro-social.svg`);
    injectMeta('property','og:image:alt', 'e-FinPro contract, invoice, TDS and reporting SaaS');
    injectMeta('name','twitter:card','summary_large_image');
    injectMeta('name','twitter:title', SEO_TITLE);
    injectMeta('name','twitter:description', SEO_DESC);
    injectMeta('name','twitter:image', `${SITE_URL}/efinpro-social.svg`);
    injectMeta('name','twitter:url', `${SITE_URL}/`);
    injectCanonical(`${SITE_URL}/`);
    injectLD({
      '@context':'https://schema.org',
      '@graph':[
        { '@type':'WebSite', name:'e-FinPro', url:`${SITE_URL}/`, description: SEO_DESC },
        { '@type':'Organization', name:'e-FinPro', url:`${SITE_URL}/`, logo:`${SITE_URL}/efinpro_logo.svg` },
        { '@type':'SoftwareApplication', name:'e-FinPro', applicationCategory:'BusinessApplication', operatingSystem:'Web', url:`${SITE_URL}/`, description: SEO_DESC,
          featureList:['Contract management','GST invoicing','TDS receipt tracking','Bank ledger','Tally export','Multi-firm access'] },
        { '@type':'FAQPage', mainEntity: FAQS.map(f => ({ '@type':'Question', name:f.q, acceptedAnswer:{'@type':'Answer',text:f.a} })) },
      ]
    });
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });
  const scrollToAuth = () => authRef.current?.scrollIntoView({ behavior:'smooth' });

  return (
    <div className="lp-root">

      {/* ── Sticky Nav ── */}
      <nav className="lp-nav">
        <div className="lp-nav-logo">
          <img src="/efinpro_logo.svg" alt="e-FinPro" />
        </div>
        <div className="lp-nav-links">
          <button className="lp-nav-link" onClick={() => scrollTo(featuresId)}>Features</button>
          <button className="lp-nav-link" onClick={() => scrollTo(howId)}>How it works</button>
          <button className="lp-nav-link" onClick={() => scrollTo(industriesId)}>Who it's for</button>
        </div>
        <div className="lp-nav-ctas">
          <button className="lp-btn-ghost" onClick={scrollToAuth}>Sign In</button>
          <button className="lp-btn-gold"  onClick={scrollToAuth}>Get Started Free</button>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="lp-hero">
        <div className="lp-hero-left">
          <div className="lp-eyebrow"><span className="lp-eyebrow-dot" />Contract-to-Cash · India-Ready</div>
          <h1 className="lp-h1">
            From Signed Contract<br/>
            to <em>Paid Invoice</em><br/>
            — One Platform
          </h1>
          <p className="lp-hero-sub">
            e-FinPro gives NGOs, government PMUs, and consulting firms a single workspace to manage agreements, raise GST invoices, track TDS receipts, and export finance-ready reports — without spreadsheets.
          </p>
          <div className="lp-hero-chips">
            {['GST Invoicing','TDS Receipts','Tally Export','Multi-Currency','Bank Ledger','Multi-Firm'].map(c => (
              <span key={c} className="lp-chip">{c}</span>
            ))}
          </div>
          <div className="lp-hero-ctas">
            <button className="lp-cta-primary" onClick={scrollToAuth}>
              Start Free — No Card Needed
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
            <button className="lp-cta-secondary" onClick={() => scrollTo(howId)}>
              See how it works
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
          </div>
          <div className="lp-hero-trust">
            <div className="lp-hero-trust-dots">
              {['#dbeafe','#dcfce7','#fee2e2','#f3e8ff'].map((c,i) => <div key={i} className="lp-trust-dot" style={{background:c}} />)}
            </div>
            Built for contract-heavy finance teams in India
          </div>
        </div>

        {/* Auth card */}
        <div ref={authRef}>
          <AuthForm onAuth={onAuth} />
        </div>
      </section>

      {/* ── Trust chips bar ── */}
      <div className="lp-trust-bar">
        <span className="lp-trust-label">Built for</span>
        <div className="lp-trust-chips">
          {['NGO Finance','PMU Billing','Consulting Contracts','Donor-Funded Programmes','Government Work Orders','Multi-Vendor Projects'].map(t => (
            <span key={t} className="lp-trust-chip">{t}</span>
          ))}
        </div>
      </div>

      {/* ── Features ── */}
      <section className="lp-section lp-section-soft" id={featuresId}>
        <div className="lp-section-head">
          <div className="lp-section-eyebrow">Platform Capabilities</div>
          <h2 className="lp-section-title">Everything a contract-led finance team needs</h2>
          <p className="lp-section-sub">Manage the complete billing lifecycle from agreement signing to receipt recording and reporting — without switching tools.</p>
        </div>
        <div className="lp-features-grid">
          {FEATURES.map(f => (
            <div key={f.title} className="lp-feature-card">
              <div className={`lp-feature-icon ${f.cls}`}>{f.icon}</div>
              <h3 className="lp-feature-title">{f.title}</h3>
              <p className="lp-feature-body">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="lp-section lp-section-white" id={howId}>
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">The Workflow</div>
          <h2 className="lp-section-title">Contract → Invoice → Receipt → Report</h2>
          <p className="lp-section-sub">A linear, connected workflow so every invoice traces back to an agreement and every receipt closes the loop.</p>
        </div>
        <div className="lp-steps-grid">
          {WORKFLOW.map(w => (
            <div key={w.n} className="lp-step-card">
              <div className="lp-step-num-circle">{w.n}</div>
              <h3 className="lp-step-title">{w.title}</h3>
              <p className="lp-step-body">{w.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Workflow visual ── */}
      <div className="lp-workflow lp-section-soft">
        <div>
          <div className="lp-section-eyebrow">Why it matters</div>
          <h2 className="lp-workflow-title">One record, from agreement to settlement</h2>
          <p className="lp-workflow-sub">Most teams track contracts in one tool, invoices in another, and receipts in a spreadsheet. e-FinPro connects all three so finance teams get a single, auditable source of truth.</p>
          <div className="lp-workflow-list">
            {['Contract ceiling and invoiced amount stay in sync in real time.','TDS deductions record against each receipt — not estimated at year end.','Ledger reflects actual bank movement, not manual data entry.','Reports pull from live data — not from a rebuilt spreadsheet.'].map((item, i) => (
              <div key={i} className="lp-workflow-item">
                <div className="lp-workflow-item-num">{i+1}</div>
                <div className="lp-workflow-item-text">{item}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="lp-workflow-visual">
          <div className="lp-wv-cards">
            {[
              { dot:'lp-wv-dot-green',  label:'QR-Based Registration Agreement', val:'₹ 9,73,000',   badge:'lp-wv-badge-green', btext:'Active' },
              { dot:'lp-wv-dot-gold',   label:'Invoice #INV/26-27/2',            val:'₹ 9,72,101',   badge:'lp-wv-badge-gold',  btext:'Pending' },
              { dot:'lp-wv-dot-blue',   label:'Receipt recorded — TDS ₹97,210',  val:'₹ 8,74,891',   badge:'lp-wv-badge-blue',  btext:'Settled' },
              { dot:'lp-wv-dot-green',  label:'Bank Balance',                    val:'₹ 8,47,738',   badge:'lp-wv-badge-green', btext:'Live' },
            ].map((r,i) => (
              <div key={i} className="lp-wv-card">
                <div className={`lp-wv-dot ${r.dot}`} />
                <span className="lp-wv-label">{r.label}</span>
                <span className="lp-wv-val">{r.val}</span>
                <span className={`lp-wv-badge ${r.badge}`}>{r.btext}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Industries ── */}
      <section className="lp-section lp-section-dark" id={industriesId}>
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">Built For</div>
          <h2 className="lp-section-title">Who uses e-FinPro</h2>
          <p className="lp-section-sub">Purpose-built for teams where contracts drive billing and every receipt has to be accounted for.</p>
        </div>
        <div className="lp-industry-grid">
          {INDUSTRIES.map(ind => (
            <div key={ind.title} className="lp-industry-card">
              <div className="lp-industry-icon">{ind.icon}</div>
              <h3 className="lp-industry-title">{ind.title}</h3>
              <p className="lp-industry-body">{ind.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="lp-section lp-section-soft">
        <div className="lp-section-head lp-section-head-center">
          <div className="lp-section-eyebrow">FAQ</div>
          <h2 className="lp-section-title">Common questions</h2>
        </div>
        <div className="lp-faq-list">
          {FAQS.map(f => <FaqItem key={f.q} {...f} />)}
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="lp-cta-section">
        <h2 className="lp-cta-h2">Start managing contracts the right way</h2>
        <p className="lp-cta-sub">Register your firm workspace in 2 minutes — no credit card, no setup fee. Add GST, team members, and signatories when you're ready.</p>
        <div className="lp-cta-btns">
          <button className="lp-cta-big lp-cta-big-gold" onClick={scrollToAuth}>Register Free →</button>
          <button className="lp-cta-big lp-cta-big-outline" onClick={() => scrollTo(featuresId)}>Explore Features</button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="lp-footer">
        <div className="lp-footer-brand">
          <img src="/triyog-logo.svg" alt="e-FinPro" className="lp-footer-logo" />
          <span className="lp-footer-name">e-FinPro</span>
        </div>
        <span className="lp-footer-copy">© {new Date().getFullYear()} Triyog Innovations Private Limited. All rights reserved.</span>
        <div className="lp-footer-links">
          <span className="lp-footer-link">Contract-to-cash SaaS for India</span>
          <span className="lp-footer-link">GST · TDS · Tally · Multi-firm</span>
        </div>
      </footer>

    </div>
  );
}
